import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, CircularProgress, Chip, Button, TextField,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, FormGroup, FormControlLabel, Checkbox,
    ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { InstitutionService } from '../../services/institution.service';
import { useToast } from '../../context/ToastContext';
import { IInstitution } from '../../interfaces/institution.interface';
import { ModuleKey } from '../dashboard/registry';
import { GATED_MODULE_KEYS, MODULE_META } from '../dashboard/registry/moduleMeta';
import { PlanService } from '../../services/plan.service';
import { FREE_ALLOWED_MODULES, FREE_EMPLOYEE_CAP, IInstitutionPlan, PlanTier } from '../../interfaces/plan.interface';
import { usePlan } from '../../context/PlanContext';
import { useFreeTierNudge } from '../shared/FreeTierNudge';

/**
 * SaaS Operator console: per-institution premium entitlement + gated-module provisioning.
 *
 * Deliberately cross-tenant — the SaaS Operator provisions on behalf of the SaaS product across
 * every institution (the backend restricts the list + write endpoints to SaaSOperator / SystemAdmin).
 * A "Manage" dialog per institution combines both writes (premium PATCH + enabled-modules PUT) into
 * one Save so the two entitlements are never left half-applied.
 *
 * Accounting is a paid add-on billed at a custom monthly fee per institution. The backend refuses to
 * enable it until a fee is set, so Save writes the fee first and only then the module set.
 *
 * The plan (Free / Paid) is part of the same Save and is written before the module set: an upgrade must
 * land before a paid module can be enabled, and a downgrade switches the paid modules off server-side.
 * Choosing Free asks for confirmation and unticks the modules the Free plan leaves out.
 */
export default function TenantProvisioning() {
    const { t } = useTranslation(['institutions', 'common']);
    const toast = useToast();
    const [rows, setRows] = useState<IInstitution[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(true);

    const [managing, setManaging] = useState<IInstitution | null>(null);
    const [dialogLoading, setDialogLoading] = useState(false);
    const [dialogPremium, setDialogPremium] = useState(false);
    const [dialogModules, setDialogModules] = useState<Set<ModuleKey>>(new Set());
    const [saving, setSaving] = useState(false);
    const [savedFee, setSavedFee] = useState<number | null>(null);
    const [feeInput, setFeeInput] = useState('');
    const [savedPlan, setSavedPlan] = useState<IInstitutionPlan | null>(null);
    const [dialogPlan, setDialogPlan] = useState<PlanTier | null>(null);
    const [confirmDowngrade, setConfirmDowngrade] = useState(false);
    const { refresh: refreshCurrentPlan } = usePlan();
    const freeTier = useFreeTierNudge();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await InstitutionService.getInstitutions(page, rowsPerPage);
            setRows(res.content ?? []);
            setTotal(res.totalElements ?? 0);
        } catch (err) {
            toast.showApiError(err, t('shared.loadInstitutionsFailed'));
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, toast, t]);

    useEffect(() => { load(); }, [load]);

    const openManage = async (institution: IInstitution) => {
        setManaging(institution);
        setDialogPremium(institution.premiumDashboardEnabled === true);
        setDialogLoading(true);
        setSavedFee(null);
        setFeeInput('');
        setSavedPlan(null);
        setDialogPlan(null);
        PlanService.getPlan(institution.id)
            .then(plan => { setSavedPlan(plan); setDialogPlan(plan.planTier); })
            .catch(err => toast.showApiError(err, t('provisioning.plan.loadFailed')));
        InstitutionService.getAccountingFee(institution.id)
            .then(fee => { setSavedFee(fee); setFeeInput(fee != null ? String(fee) : ''); })
            .catch(err => toast.showApiError(err, t('provisioning.loadFeeFailed')));
        try {
            const modules = await InstitutionService.getEnabledModules(institution.id);
            setDialogModules(new Set(modules.filter((m): m is ModuleKey => (GATED_MODULE_KEYS as string[]).includes(m))));
        } catch (err) {
            toast.showApiError(err, t('provisioning.loadModulesFailed'));
            setDialogModules(new Set());
        } finally {
            setDialogLoading(false);
        }
    };

    const toggleModule = (key: ModuleKey) => {
        setDialogModules(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    // Modules the Free plan allows: the server's answer once the institution is FREE, else the known policy.
    const freeModules = savedPlan?.planTier === 'FREE' ? savedPlan.allowedModules : FREE_ALLOWED_MODULES;
    const blockedOnPlan = (key: ModuleKey) => dialogPlan === 'FREE' && !freeModules.includes(key);
    const modulesDroppedByDowngrade = GATED_MODULE_KEYS.filter(k => dialogModules.has(k) && !freeModules.includes(k));

    const choosePlan = (next: PlanTier | null) => {
        if (!next || next === dialogPlan) return;
        if (next === 'FREE') setConfirmDowngrade(true);
        else setDialogPlan(next);
    };

    const applyDowngrade = () => {
        setDialogPlan('FREE');
        setDialogModules(prev => new Set(Array.from(prev).filter(k => freeModules.includes(k))));
        setConfirmDowngrade(false);
    };

    const fee = feeInput.trim() === '' ? null : Number(feeInput);
    const feeInvalid = fee !== null && !(Number.isFinite(fee) && fee > 0);
    const accountingNeedsFee = dialogModules.has('accounting') && (fee === null || feeInvalid);

    const save = async () => {
        if (!managing) return;
        setSaving(true);
        try {
            if (fee !== null && fee !== savedFee) {
                setSavedFee(await InstitutionService.setAccountingFee(managing.id, fee));
            }
            if (dialogPlan && dialogPlan !== savedPlan?.planTier) {
                setSavedPlan(await PlanService.setPlan(managing.id, dialogPlan));
                refreshCurrentPlan();
            }
            await Promise.all([
                InstitutionService.setPremiumDashboard(managing.id, dialogPremium),
                InstitutionService.setEnabledModules(managing.id, Array.from(dialogModules)),
            ]);
            setRows(prev => prev.map(r => (r.id === managing.id ? { ...r, premiumDashboardEnabled: dialogPremium } : r)));
            toast.showSuccess(t('provisioning.saved', { name: managing.name }));
            setManaging(null);
        } catch (err) {
            if (!freeTier.handle(err)) toast.showApiError(err, t('provisioning.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WorkspacePremiumIcon color="primary" />
                <Typography variant="h5">{t('provisioning.title')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('provisioning.intro')}
            </Typography>

            <Paper>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('provisioning.columns.institution')}</TableCell>
                                        <TableCell>{t('provisioning.columns.code')}</TableCell>
                                        <TableCell align="center">{t('provisioning.columns.premiumDashboard')}</TableCell>
                                        <TableCell align="center">{t('provisioning.columns.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">{t('provisioning.empty')}</TableCell>
                                        </TableRow>
                                    ) : rows.map(inst => (
                                        <TableRow key={inst.id} hover>
                                            <TableCell>{inst.name}</TableCell>
                                            <TableCell>{inst.code}</TableCell>
                                            <TableCell align="center">
                                                {inst.premiumDashboardEnabled
                                                    ? <Chip size="small" color="success" label={t('provisioning.enabled')} />
                                                    : <Chip size="small" variant="outlined" label={t('provisioning.disabled')} />}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button size="small" onClick={() => openManage(inst)}>{t('provisioning.manage')}</Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50]}
                        />
                    </>
                )}
            </Paper>

            <Dialog open={managing !== null} onClose={() => setManaging(null)} fullWidth maxWidth="xs" closeAfterTransition={false}>
                <DialogTitle>{t('provisioning.manageTitle', { name: managing?.name ?? '' })}</DialogTitle>
                <DialogContent dividers>
                    {dialogLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
                    ) : (
                        <FormGroup>
                            <Typography variant="subtitle2" sx={{ mb: 1 }} id="plan-label">{t('provisioning.plan.label')}</Typography>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                color="primary"
                                value={dialogPlan}
                                onChange={(_e, next: PlanTier | null) => choosePlan(next)}
                                aria-labelledby="plan-label"
                            >
                                <ToggleButton value="FREE">{t('provisioning.plan.free')}</ToggleButton>
                                <ToggleButton value="PAID">{t('provisioning.plan.paid')}</ToggleButton>
                            </ToggleButtonGroup>
                            {dialogPlan && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                                    {dialogPlan === 'FREE'
                                        ? t('provisioning.plan.freeHelp', { cap: savedPlan?.employeeCap ?? FREE_EMPLOYEE_CAP })
                                        : t('provisioning.plan.paidHelp')}
                                </Typography>
                            )}
                            <FormControlLabel
                                control={<Checkbox checked={dialogPremium} onChange={(e) => setDialogPremium(e.target.checked)} />}
                                label={t('provisioning.premiumDashboard')}
                            />
                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>{t('provisioning.enabledModules')}</Typography>
                            {GATED_MODULE_KEYS.map(key => {
                                const blocked = blockedOnPlan(key);
                                const label = t(MODULE_META[key].labelKey, { ns: 'dashboard' });
                                return (
                                    <FormControlLabel
                                        key={key}
                                        disabled={blocked}
                                        control={<Checkbox checked={dialogModules.has(key)} onChange={() => toggleModule(key)} />}
                                        label={blocked ? (
                                            <span>
                                                {label}{' '}
                                                <Typography component="span" variant="caption" color="text.secondary">
                                                    ({t('provisioning.plan.notOnFree')})
                                                </Typography>
                                            </span>
                                        ) : label}
                                    />
                                );
                            })}
                            <TextField
                                id="accounting-fee"
                                label={t('provisioning.accountingFee')}
                                size="small"
                                value={feeInput}
                                onChange={(e) => setFeeInput(e.target.value)}
                                inputProps={{ inputMode: 'decimal' }}
                                error={feeInvalid || accountingNeedsFee}
                                helperText={accountingNeedsFee
                                    ? t('provisioning.feeRequired')
                                    : t('provisioning.feeHelp')}
                                sx={{ mt: 2 }}
                            />
                        </FormGroup>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setManaging(null)} disabled={saving}>{t('common:actions.cancel')}</Button>
                    <Button onClick={save} disabled={saving || dialogLoading || feeInvalid || accountingNeedsFee} variant="contained">{t('common:actions.save')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmDowngrade}
                onClose={() => setConfirmDowngrade(false)}
                maxWidth="xs"
                fullWidth
                closeAfterTransition={false}
                aria-labelledby="downgrade-title"
            >
                <DialogTitle id="downgrade-title">{t('provisioning.plan.downgradeTitle', { name: managing?.name ?? '' })}</DialogTitle>
                <DialogContent>
                    {modulesDroppedByDowngrade.length > 0 && (
                        <DialogContentText sx={{ mb: 1.5 }}>
                            {t('provisioning.plan.downgradeModules', {
                                modules: modulesDroppedByDowngrade.map(k => t(MODULE_META[k].labelKey, { ns: 'dashboard' })).join(', '),
                            })}
                        </DialogContentText>
                    )}
                    <DialogContentText sx={{ mb: 1.5 }}>
                        {t('provisioning.plan.downgradeEmployees', { cap: FREE_EMPLOYEE_CAP })}
                    </DialogContentText>
                    <DialogContentText>{t('provisioning.plan.downgradeDevices')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDowngrade(false)}>{t('common:actions.cancel')}</Button>
                    <Button onClick={applyDowngrade} color="warning" variant="contained">{t('provisioning.plan.confirmDowngrade')}</Button>
                </DialogActions>
            </Dialog>

            {freeTier.element}
        </Box>
    );
}
